/**
 * Shared API client for the Decoded frontend.
 *
 * Responsibilities:
 *  - Reads REACT_APP_BACKEND_URL once and normalises paths.
 *  - Attaches Authorization: Bearer <token> from localStorage automatically.
 *  - Sets Content-Type: application/json when a JSON body is provided.
 *  - Handles 401 responses centrally (clears auth + redirects to /login).
 *  - Throws a consistent ApiError with the backend's error message
 *    for any non-2xx response so callers can `try/catch` uniformly.
 *  - Sends credentials so future httpOnly-cookie migration is a no-op.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'decoded_token';
const USER_KEY = 'decoded_user';

export class ApiError extends Error {
  constructor(status, message, payload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore storage failures */
  }
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  // Skip redirect if already on an auth page or the public pre-launch wrapper.
  if (/^\/(login|signup|forgot-password|reset-password|join-org|report|resources|terms|privacy)/i.test(path)) return;
  if (path === '/') return;
  window.location.href = '/login';
}

async function parseBody(res) {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
}

function extractMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === 'string') return payload || fallback;
  if (typeof payload.detail === 'string') return payload.detail;
  if (typeof payload.message === 'string') return payload.message;
  if (Array.isArray(payload.detail)) {
    // FastAPI validation errors → concise summary
    return payload.detail
      .map((d) => (d && d.msg) || JSON.stringify(d))
      .join('; ');
  }
  return fallback;
}

/**
 * Low-level request. Prefer apiGet/apiPost/apiPut/apiDelete.
 *
 * @param {string} path        Path starting with "/api/..." or absolute URL.
 * @param {object} [options]
 * @param {string} [options.method]      HTTP verb (default GET).
 * @param {object|FormData} [options.body]  Body — JSON-encoded unless FormData.
 * @param {object} [options.headers]     Extra headers.
 * @param {boolean} [options.auth=true]  Attach Bearer token if available.
 * @param {boolean} [options.raw=false]  Return the Response object instead of parsed body.
 * @param {AbortSignal} [options.signal] Abort controller signal.
 */
export async function apiFetch(path, options = {}) {
  const {
    method = 'GET',
    body,
    headers = {},
    auth = true,
    raw = false,
    signal,
  } = options;

  const url = path.startsWith('http') ? path : `${API_URL}${path}`;
  const finalHeaders = { Accept: 'application/json', ...headers };

  let finalBody = body;
  if (body && !(body instanceof FormData) && typeof body !== 'string') {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] || 'application/json';
    finalBody = JSON.stringify(body);
  }

  if (auth) {
    const token = getToken();
    if (token && !finalHeaders.Authorization) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(url, {
    method,
    headers: finalHeaders,
    body: finalBody,
    credentials: 'include',
    signal,
  });

  if (raw) return res;

  if (res.status === 401 && auth) {
    clearAuth();
    redirectToLogin();
    const payload = await parseBody(res);
    throw new ApiError(401, extractMessage(payload, 'Session expired'), payload);
  }

  if (!res.ok) {
    const payload = await parseBody(res);
    throw new ApiError(res.status, extractMessage(payload, res.statusText || 'Request failed'), payload);
  }

  if (res.status === 204) return null;
  return parseBody(res);
}

export const apiGet = (path, opts = {}) => apiFetch(path, { ...opts, method: 'GET' });
export const apiPost = (path, body, opts = {}) => apiFetch(path, { ...opts, method: 'POST', body });
export const apiPut = (path, body, opts = {}) => apiFetch(path, { ...opts, method: 'PUT', body });
export const apiPatch = (path, body, opts = {}) => apiFetch(path, { ...opts, method: 'PATCH', body });
export const apiDelete = (path, opts = {}) => apiFetch(path, { ...opts, method: 'DELETE' });

export { API_URL };
